import Icon from '@/components/ui/AppIcon';

interface ConsultationOption {
  id: string
  icon: string
  title: string
  description: string
  duration: string
  link: string
}

const BookingSection = () => {
  const consultationOptions: ConsultationOption[] = [
    {
      id: 'option_whatsapp',
      icon: 'ChatBubbleLeftRightIcon',
      title: 'WhatsApp Call',
      description: 'Connect via WhatsApp for instant communication',
      duration: '30 minutes',
      link: 'https://wa.me/18634409462',
    },
  ]

  return (
    <section className="py-32 bg-background min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        {/* Page Header */}
        <div className="reveal text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold font-montserrat text-foreground mb-6">
            Book a Free <span className="text-primary">Consultation</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Schedule a consultation with our container experts to discuss your project, get recommendations, and receive a personalized quote.
          </p>
        </div>

        {/* Consultation Options */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {consultationOptions.map((option, index) => (
            <a
              key={option.id}
              href={option.link}
              target={option.link.startsWith('http') ? '_blank' : undefined}
              rel={option.link.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="reveal container-card p-8 text-center hover:scale-105 transition-all cursor-pointer group"
              style={{ animationDelay: `${0.1 * (index + 1)}s` }}
            >
              {/* Icon */}
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-primary group-hover:scale-110 transition-all">
                <Icon name={option.icon as any} size={32} className="text-primary group-hover:text-primary-foreground transition-colors" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold font-montserrat text-foreground mb-2">
                {option.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {option.description}
              </p>

              {/* Duration */}
              <div className="inline-flex items-center gap-2 text-xs text-primary font-semibold bg-primary/10 rounded-full px-4 py-2">
                <Icon name="ClockIcon" size={14} />
                {option.duration}
              </div>
            </a>
          ))}
        </div>

        {/* WhatsApp Highlight */}
        <div className="reveal container-card p-8 bg-success/5 border-2 border-success/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-success/10 flex items-center justify-center flex-shrink-0">
                <Icon name="ChatBubbleLeftRightIcon" size={32} className="text-success" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground mb-1">
                  Prefer Instant Messaging?
                </h3>
                <p className="text-muted-foreground">
                  Chat with us on WhatsApp for quick questions and updates
                </p>
              </div>
            </div>
            <a
              href="https://wa.me/18634409462"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-success text-success-foreground font-semibold rounded-full px-6 py-3 hover:bg-success/90 transition-all whitespace-nowrap"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={20} />
              863-440-9462 (WhatsApp only)
            </a>
          </div>
        </div>

        {/* Calendly Embed Placeholder */}
        <div id="calendly-embed" className="reveal mt-16 container-card p-12 text-center">
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Icon name="CalendarIcon" size={40} className="text-primary" />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-4">
            Schedule Your Video Consultation
          </h3>
          <p className="text-muted-foreground mb-8">
            Calendly booking widget will be integrated here
          </p>
          <div className="h-96 bg-gray-100 rounded-xl flex items-center justify-center">
            <p className="text-muted-foreground">
              [Calendly Embed Integration]
            </p>
          </div>
        </div>

        {/* What to Expect */}
        <div className="reveal mt-16">
          <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
            What to Expect During Your Consultation
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircleIcon" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">Project Assessment</h4>
                <p className="text-sm text-muted-foreground">
                  We'll discuss your business goals, location, and specific requirements
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircleIcon" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">Model Recommendations</h4>
                <p className="text-sm text-muted-foreground">
                  Get expert advice on which container size and features suit your needs
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircleIcon" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">Pricing & Timeline</h4>
                <p className="text-sm text-muted-foreground">
                  Receive a detailed quote and realistic production timeline
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircleIcon" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">Next Steps</h4>
                <p className="text-sm text-muted-foreground">
                  Learn about our process, payment options, and delivery logistics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default BookingSection